export const DEFAULT_STATSIG_ECOMM_SSR_DYNAMIC_CONFIG = {
  isSignupOTPRequired: true,
  globalSignOutEnabled: true,
  disableCartEDDMessaging: true,
  useCustomerSearch: false,
  disableForceEDDForAgeRestrictedProducts: false,
  disableAgeVerification: false,
  showMultipleShipmentsInOrderDetails: false,
  showMultipleShipmentsInOrderList: false,
  useEfaKeyForEfaRecommendations: true,
  imageUploadLimitReturn: 3,
  useRefundbackendCalculations: false,
  isNewCartCollectionsEnabled: false,
  hiddenMenuItems: [] as { filterKey: string; filterValue: string }[],
  fmoOrderSearchPeriodValue: 2,
  fmoOrderSearchPeriodUnit: 'years' as 'days' | 'years',
  fingerprintCacheTTL: 86400000, // 1 day
  enableFMOForGuestUser: false,
  enableCarrierAlignedStatus: false,
  showD2cReviewsOnB2B: false,
  // Country codes (ISO-3166 alpha-2) whose addresses have no customer-facing province code,
  // so it's hidden per-address on order details (e.g. UK "ENG"). Mirrors checkout's no-state-field list.
  countriesWithoutProvinceCode: ['GB'] as string[],
  enableWriteReviewOnB2BPdp: false,
  // Safety cap for PL eager layout pagination (home): max pages the eager loop may drain from
  // mount before standing down to normal scroll-triggered pagination. 0 disables eager draining.
  plEagerLayoutPaginationMaxPages: 5,
  // Flowers printed card (PDP): max message length in UTF-16 code units. Default mirrors
  // PRINTED_CARD_MESSAGE_MAX_LENGTH in src/config/gifting/printed-card.ts and the cart-service
  // @SafeText 200-char cap — keep the three in sync when tuning.
  printedCardMessageMaxLength: 200,
}

/**
 * Tunables for the Partytown third-party-script offload (`ssr-partytown` dynamic config).
 * Resolved user-agnostically at SSR, but only for requests already in the Partytown bucket of
 * `all__fb-pixel_partytown-offload__web` — control-group requests carry none of this.
 *
 * These defaults are the values the offload shipped with, so an absent or unreachable Statsig
 * config leaves behaviour byte-identical. Values are read by `_document` (loader snippet) and by
 * FbPixel (worker bootstrap), and are clamped/filtered by `resolvePartytownConfig` before use —
 * they reach an inline script, so they are never trusted verbatim.
 */
export const DEFAULT_STATSIG_PARTYTOWN_DYNAMIC_CONFIG = {
  /**
   * Meta `autoConfig` — automatic events (SubscribedButtonClick) + microdata enrichment, running
   * inside the worker rather than on the main thread.
   *
   * WARNING: this applies to the Partytown path ONLY; the main-thread path never sets it and Meta
   * defaults it on. Turning it off mid-experiment therefore removes those events from the Partytown
   * bucket alone — that changes WHAT the experiment measures, not just where scripts run, and it
   * contaminates the pixel-delivery parity check. Treat a change here as an experiment restart.
   */
  fbPixelAutoConfig: true,
  /**
   * How long a document waits for the Partytown sandbox before Partytown replays `text/partytown`
   * scripts on the main thread (Partytown's own default is ~10s). Emitted into the loader snippet.
   */
  fallbackTimeoutMs: 5000,
  /**
   * Extra delay after `fallbackTimeoutMs` before FbPixel gives up on the worker and initialises
   * fbevents on the main thread. Must stay positive so the client watchdog fires strictly after
   * Partytown's own fallback — otherwise both paths race to initialise the pixel.
   */
  pixelWatchdogGraceMs: 1500,
  /**
   * Main-thread stubs Partytown installs to relay calls into the worker. Future pixel rollouts add
   * their global here (e.g. `ttq` for TikTok) — no code change needed, but the corresponding pixel
   * must also be tagged `text/partytown`.
   */
  forward: ['fbq'] as string[],
}
